import { TEMPLATES, type TemplateId } from '@finsim/model';
import { Command } from 'cmdk';
import { useUnit } from 'effector-react';
import {
  ArrowRight,
  BookOpen,
  CopyPlus,
  Compass,
  Dices,
  FileDown,
  FolderOpen,
  Keyboard,
  Languages,
  Layers,
  Palette,
  Plus,
  Redo2,
  Share2,
  SlidersHorizontal,
  Sparkles,
  TrendingUp,
  Undo2,
} from 'lucide-react';
import { Dialog as RadixDialog } from 'radix-ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $locale,
  $theme,
  localeChanged,
  THEME_PREFERENCES,
  themeChanged,
} from '../../entities/preferences';
import { $project, $readOnly, redo, undo } from '../../entities/project';
import {
  $focusedScenarioId,
  assumptionsOpened,
  itemSelected,
  scenarioInspected,
  sharedItemsOpened,
} from '../../entities/selection';
import {
  $simMode,
  $valueMode,
  simModeToggled,
  valueModeToggled,
  viewChanged,
  visibleViews,
} from '../../entities/view-settings';
import { addItemRequested, TEMPLATE_MENU } from '../../features/add-item';
import { returnsAnalysisRequested } from '../../features/analyse-returns';
import { addVariantRequested } from '../../features/manage-variants';
import { saveCopyRequested } from '../../features/save-copy';
import { shareDialogOpened } from '../../features/share-project';
import { useHotkeys } from '../../shared/lib/hotkeys';
import { LOCALES } from '../../shared/lib/locale';
import { shortcutOf, type ShortcutId } from '../../shared/lib/shortcuts';
import { routes } from '../../shared/routing';
import { reportDialogOpened } from '../report';

import { helpOpened, ShortcutKeys, tourRequested } from '../help-center';
import { $paletteOpen, paletteClosed, paletteOpened } from './model';

const LANGUAGE_NAME = { en: 'English', bg: 'Български' } as const;

/** Global palette and shortcuts sheet, available on every page. */
export function CommandPalette() {
  const [open, openPalette, close] = useUnit([$paletteOpen, paletteOpened, paletteClosed]);
  useHotkeys({ 'mod+k': openPalette });
  return (
    <>
      <RadixDialog.Root
        open={open}
        onOpenChange={(next) => {
          if (!next) close();
        }}
      >
        {open && <PaletteContent />}
      </RadixDialog.Root>
    </>
  );
}

interface Entry {
  id: string;
  label: string;
  icon: ReactNode;
  run: () => void;
  keywords?: string[];
  shortcut?: ShortcutId;
}

function useEntries(): { heading: string; entries: Entry[] }[] {
  const { t } = useTranslation();
  const units = useUnit({
    project: $project,
    readOnly: $readOnly,
    focused: $focusedScenarioId,
    valueMode: $valueMode,
    simMode: $simMode,
    theme: $theme,
    locale: $locale,
    view: viewChanged,
    toggleValue: valueModeToggled,
    toggleSim: simModeToggled,
    addItem: addItemRequested,
    addVariant: addVariantRequested,
    assumptions: assumptionsOpened,
    shared: sharedItemsOpened,
    selectItem: itemSelected,
    inspectVariant: scenarioInspected,
    undo,
    redo,
    setTheme: themeChanged,
    setLocale: localeChanged,
    help: helpOpened,
    tour: tourRequested,
    home: routes.home.open,
    saveCopy: saveCopyRequested,
    share: shareDialogOpened,
    report: reportDialogOpened,
    analyseReturns: returnsAnalysisRequested,
  });
  const { project, focused, readOnly } = units;
  const groups: { heading: string; entries: Entry[] }[] = [];

  if (project) {
    const scenarioId = focused ?? project.scenarios[0]?.id;
    const editing: Entry[] = [
      ...(scenarioId
        ? TEMPLATE_MENU.flatMap(({ ids }) => ids).map((templateId: TemplateId): Entry => ({
            id: `new-${templateId}`,
            label: t('palette.newItem', { name: t(`templates.${templateId}`) }),
            icon: <Plus />,
            keywords: [TEMPLATES[templateId].group],
            shortcut: 'createItem',
            run: () => {
              units.addItem({
                scenarioId,
                templateId,
                name: t(`templates.${templateId}`),
                contributionsName: t('tax.contributions'),
              });
            },
          }))
        : []),
      {
        id: 'new-variant',
        label: t('variants.newVariant'),
        icon: <Layers />,
        shortcut: 'newVariant',
        run: () => {
          units.addVariant({
            name: t('variants.defaultName', { n: project.scenarios.length + 1 }),
          });
        },
      },
    ];
    const actions: Entry[] = [
      ...(readOnly ? [] : editing),
      ...(readOnly
        ? []
        : [{ id: 'share', label: t('share.palette'), icon: <Share2 />, run: units.share }]),
      ...(readOnly
        ? [
            {
              id: 'save-copy',
              label: t('viewer.saveCopy'),
              icon: <CopyPlus />,
              run: units.saveCopy,
            },
          ]
        : []),
      ...(readOnly || project.investments.length > 0
        ? []
        : [
            {
              id: 'analyse-returns',
              label: t('returns.analyse'),
              icon: <TrendingUp />,
              keywords: ['irr', 'roi', 'yield'],
              run: () => {
                units.analyseReturns({ name: project.name });
              },
            },
          ]),
      {
        id: 'export-report',
        label: t('report.dialog.palette'),
        icon: <FileDown />,
        keywords: ['pdf', 'print'],
        shortcut: 'exportReport',
        run: units.report,
      },
      {
        id: 'assumptions',
        label: t('assumptions.title'),
        icon: <SlidersHorizontal />,
        shortcut: 'assumptions',
        run: units.assumptions,
      },
      { id: 'shared', label: t('variants.sharedItems'), icon: <Layers />, run: units.shared },
      ...(readOnly
        ? []
        : [
            {
              id: 'undo',
              label: t('history.undo'),
              icon: <Undo2 />,
              shortcut: 'undo' as const,
              run: units.undo,
            },
            {
              id: 'redo',
              label: t('history.redo'),
              icon: <Redo2 />,
              shortcut: 'redo' as const,
              run: units.redo,
            },
          ]),
    ];
    groups.push({ heading: t('palette.groups.actions'), entries: actions });

    const goTo: Entry[] = [
      ...visibleViews(project).map((v) => ({
        id: `view-${v}`,
        label: t('palette.goToView', { name: t(`views.${v}`) }),
        icon: <ArrowRight />,
        run: () => {
          units.view(v);
        },
      })),
      ...project.scenarios.map((s) => ({
        id: `variant-${s.id}`,
        label: t('palette.variant', { name: s.name }),
        icon: <Layers />,
        run: () => {
          units.inspectVariant(s.id);
        },
      })),
      ...project.scenarios.flatMap((s) =>
        s.itemIds.flatMap((itemId) => {
          const item = project.items[itemId];
          return item
            ? [
                {
                  id: `item-${s.id}-${itemId}`,
                  label: t('palette.item', { name: item.name, variant: s.name }),
                  icon: <ArrowRight />,
                  run: () => {
                    units.selectItem({ itemId, scenarioId: s.id });
                  },
                },
              ]
            : [];
        }),
      ),
    ];
    groups.push({ heading: t('palette.groups.goTo'), entries: goTo });

    groups.push({
      heading: t('palette.groups.view'),
      entries: [
        {
          id: 'value-mode',
          label: units.valueMode === 'nominal' ? t('palette.showReal') : t('palette.showNominal'),
          icon: <ArrowRight />,
          shortcut: 'realNominal',
          run: units.toggleValue,
        },
        {
          id: 'sim-mode',
          label:
            units.simMode === 'deterministic'
              ? t('palette.runMonteCarlo')
              : t('palette.deterministic'),
          icon: <Dices />,
          shortcut: 'monteCarlo',
          run: units.toggleSim,
        },
      ],
    });
  }

  groups.push({
    heading: t('palette.groups.preferences'),
    entries: [
      ...THEME_PREFERENCES.filter((theme) => theme !== units.theme).map((theme) => ({
        id: `theme-${theme}`,
        label: t('palette.theme', {
          name: t(
            theme === 'dark'
              ? 'settings.themeDark'
              : theme === 'light'
                ? 'settings.themeLight'
                : 'settings.themeSystem',
          ),
        }),
        icon: <Palette />,
        run: () => {
          units.setTheme(theme);
        },
      })),
      ...LOCALES.filter((l) => l !== units.locale).map((l) => ({
        id: `locale-${l}`,
        label: t('palette.language', { name: LANGUAGE_NAME[l] }),
        icon: <Languages />,
        run: () => {
          units.setLocale(l);
        },
      })),
    ],
  });
  groups.push({
    heading: t('palette.groups.help'),
    entries: [
      {
        id: 'welcome',
        label: t('help.menuItems.welcome'),
        icon: <Sparkles />,
        keywords: ['help', 'about', 'kasora'],
        run: () => {
          units.help('welcome');
        },
      },
      {
        id: 'tutorial',
        label: t('help.menuItems.tutorial'),
        icon: <BookOpen />,
        keywords: ['help', 'guide'],
        run: () => {
          units.help('tutorial');
        },
      },
      {
        id: 'shortcuts',
        label: t('shortcuts.title'),
        icon: <Keyboard />,
        shortcut: 'help',
        run: () => {
          units.help('shortcuts');
        },
      },
      ...(project && !readOnly
        ? [{ id: 'tour', label: t('palette.tour'), icon: <Compass />, run: units.tour }]
        : []),
      {
        id: 'projects',
        label: t('palette.projects'),
        icon: <FolderOpen />,
        run: () => {
          void units.home();
        },
      },
    ],
  });
  return groups;
}

function PaletteContent() {
  const { t } = useTranslation();
  const close = useUnit(paletteClosed);
  const groups = useEntries();
  return (
    <RadixDialog.Portal>
      <RadixDialog.Overlay className="fixed inset-0 z-40 animate-fade-in bg-overlay" />
      <RadixDialog.Content
        aria-describedby={undefined}
        className="fixed top-[14vh] left-1/2 z-50 w-[min(640px,calc(100vw-32px))] -translate-x-1/2 animate-scale-in overflow-hidden rounded-lg bg-bg-2 text-text-1 shadow-popover outline-none"
      >
        <RadixDialog.Title className="sr-only">{t('palette.title')}</RadixDialog.Title>
        <Command label={t('palette.title')} loop>
          <Command.Input
            autoFocus
            placeholder={t('palette.placeholder')}
            className="h-11 w-full border-b border-border-subtle bg-transparent px-4 text-sm outline-none placeholder:text-text-4"
          />
          <Command.List className="max-h-[min(420px,60vh)] overflow-y-auto p-1.5">
            <Command.Empty className="px-3 py-6 text-center text-sm text-text-3">
              {t('palette.empty')}
            </Command.Empty>
            {groups.map((group) => (
              <Command.Group
                key={group.heading}
                heading={group.heading}
                className="[&_[cmdk-group-heading]]:px-2.5 [&_[cmdk-group-heading]]:pt-2 [&_[cmdk-group-heading]]:pb-1 [&_[cmdk-group-heading]]:text-2xs [&_[cmdk-group-heading]]:font-medium [&_[cmdk-group-heading]]:text-text-3"
              >
                {group.entries.map((entry) => {
                  const shortcut = entry.shortcut ? shortcutOf(entry.shortcut) : undefined;
                  return (
                    <Command.Item
                      key={entry.id}
                      value={`${entry.label} ${entry.id}`}
                      keywords={entry.keywords ?? []}
                      onSelect={() => {
                        close();
                        entry.run();
                      }}
                      className="flex h-9 cursor-pointer items-center gap-2.5 rounded-md px-2.5 text-sm text-text-2 aria-selected:bg-bg-4 aria-selected:text-text-1 [&_svg]:size-4 [&_svg]:text-text-3"
                    >
                      {entry.icon}
                      <span className="min-w-0 flex-1 truncate">{entry.label}</span>
                      {shortcut && <ShortcutKeys def={shortcut} />}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </RadixDialog.Content>
    </RadixDialog.Portal>
  );
}
